import React, { useCallback, useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useParams } from 'react-router-dom';
import { Col, Row } from 'react-bootstrap';
import { LoaderIcon } from '../Components/Global/Icons';
import { FadeAlert } from '../Components/Global/Transitions';
import {
	getProjectByTestId,
	getprojectbyidforuser,
	getprojectforuser,
} from '../Services/PtaasApiService';
import Tests from '../Models/Tests';
import RetestList from '../Components/Tables/RetestList';
import ProjectPreReqs from '../Components/Overview/ProjectPreReqs';
import ProjectTestTypes from '../Components/Overview/ProjectTestTypes';
import ProjectCommercials from '../Components/Overview/ProjectCommercials';
import UsersWidget from '../Components/Users/UsersWidget';
import { isAccessAllowed } from '../Utilities/AccessControl';
import { useUser } from '../Components/Global/RoleContext';

// The retest table's own destination, reached from the sidebar. It used to be buried inside the
// Progress accordion on the project overview, where it was invisible until that panel was opened.
const ProjectRetestListPage: React.FC = () => {
	const { id, canopyid } = useParams();
	const { instance, accounts } = useMsal();
	const { myApplicationRole } = useUser();
	const [test, setTest] = useState<Tests>();
	// Canopy's project id, needed to link a retest finding through to its full report entry. A
	// project that never reached Canopy has none, and the findings route it would build
	// (/projects/:id/:canopyid/reports/…) would 404 — so it stays undefined and the finding
	// titles render as plain text instead of a broken link.
	const [fetchedCanopyId, setFetchedCanopyId] = useState<number | undefined>(undefined);
	// The URL wins when it has one: arriving from a project page carries it already, and trusting
	// it means the links work on the first paint rather than after a second round trip.
	const canopyId = canopyid ? Number(canopyid) : fetchedCanopyId;
	const [loading, setLoading] = useState(true);
	const [fetchError, setFetchError] = useState(false);

	const matchingRole = myApplicationRole?.find((role) => role?.id === test?.ticketno);
	const canAccess = isAccessAllowed(matchingRole ? matchingRole.role : null);
	const status = test?.status?.trim() ?? '';
	const testTypes = test?.testType ?? [];
	const assignments = test?.assignment ?? [];

	const fetchProject = useCallback(async (): Promise<void> => {
		if (!id || accounts.length === 0) return;
		setLoading(true);
		setFetchError(false);

		try {
			// Same two-step as ProjectOverviewPage, and for the same reason: the list endpoint is
			// readable by every role including Project Viewer, but carries a thinner project than
			// the widgets need. Access is decided from the list result, then the fuller version is
			// fetched only if the caller is entitled to it.
			const tests = await getprojectforuser(instance, accounts[0]);
			const found = tests?.find((t) => t.id === id);
			if (!found) {
				setFetchError(true);
				return;
			}

			const role = myApplicationRole?.find((r) => r?.id === found.ticketno);
			setTest(isAccessAllowed(role?.role ?? null) ? await getprojectbyidforuser(instance, accounts[0], id) : found);
		} catch {
			setFetchError(true);
			return;
		} finally {
			setLoading(false);
		}

		// Only when the URL did not already carry it — reached from a sub-page that has no Canopy id
		// of its own, such as /projects/:id/quote. Deliberately separate, and its failure swallowed:
		// a project with no Canopy record is a normal state, not a page error. Losing it costs the
		// finding links and nothing else.
		if (canopyid) return;
		try {
			const project = await getProjectByTestId(instance, accounts[0], id);
			setFetchedCanopyId(project?.id);
		} catch {
			setFetchedCanopyId(undefined);
		}
	}, [id, canopyid, instance, accounts, myApplicationRole]);

	useEffect(() => {
		fetchProject();
	}, [fetchProject]);

	if (loading) return <LoaderIcon />;

	return (
		<>
			<Row className='mb-4 pb-3'>
				<Col sm={8} as='h1'>{test?.projectName ?? ''}</Col>
			</Row>
			<Row>
				<Col xl={8}>
					<FadeAlert show={fetchError} dismissFunction={() => setFetchError(false)} />
					{id && <RetestList projectId={id} projectStatus={status} canopyId={canopyId} />}
				</Col>
				{/* The same widget column as the project overview, in the same order. A retest is
				    still the project, so the prerequisites, test types, commercials and users all
				    read the same here — and the client should not have to leave the retest screen to
				    see them. Skipped entirely without a project: every one of them would be reading
				    fields off nothing. */}
				{test && (
					<Col md={10} lg={8} xl={4}>
						{['Quote Requested', 'Quote Available'].includes(status)
							? <ProjectCommercials isdisable={!canAccess} status={status} /> : null}
						{canAccess ? <ProjectPreReqs assignments={assignments} isdisable={!canAccess} /> : null}
						<ProjectTestTypes
							test={test}
							testTypes={testTypes}
							isDisabled
							canAccess={!canAccess}
							OnProjectTestTypeChange={fetchProject}
						/>
						{['Quote Requested', 'Quote Available'].includes(status)
							? null : <ProjectCommercials isdisable={!canAccess} status={status} />}
						{/* ticketno is 'PRISM-<n>'; the widget wants the number. Guarded because a
						    project without one would otherwise throw on .slice rather than simply
						    render no users. */}
						{canAccess && test.ticketno
							? <UsersWidget isdisable id={Number(test.ticketno.slice(6))} /> : null}
					</Col>
				)}
			</Row>
		</>
	);
};

export default ProjectRetestListPage;
